import type amplitude from 'amplitude-js';
import * as sessionReplay from '@amplitude/session-replay-browser';
import { AMPLITUDE_API_KEY } from 'Config';
import { identifyNative, isNative, resetNative } from './native';

export const getActionSource = (): string => {
  return isNative() ? 'app' : 'website';
};

export const setIdentify = ({
  userId,
  email,
  phone,
  traits = {},
  userHash,
}: {
  userId: string;
  email: string;
  phone: string;
  traits?: Record<string, unknown>;
  userHash?: string;
}): void => {
  if (window?.analytics) {
    window.analytics.identify(userId, {
      phone,
      email,
      ...traits,
      user_hash: userHash,
    });
  }
  // Setup In-App Messaging, this is the ONLY method needed for customer.io
  if (window?._cio?.identify) {
    window._cio.identify({ id: userId, email });
  }
  if (isNative()) {
    identifyNative({ userId, email });
  }
};

export const resetAnalytics = (): void => {
  if (typeof window !== 'undefined' && window.analytics) {
    window.analytics.reset();
  }
  if (isNative()) {
    resetNative();
  }
};

export const setTrack = (trackName: string, traits = {}): void => {
  const integrations = {
    All: true,
    Intercom: false,
  };
  if (typeof window !== 'undefined' && window.analytics) {
    window.analytics.track(
      trackName,
      {
        ...traits,
        action_source: getActionSource(),
      },
      { integrations }
    );
  }
};

export const setPageLoaded = ({
  page,
  isPreviousYear,
}: {
  page: string;
  isPreviousYear: boolean;
}): void => {
  if (typeof window !== 'undefined' && window.analytics) {
    window.analytics.page({
      page,
      isPreviousYear,
      action_source: getActionSource(),
    });
  }
};

export const getAnalyticsTrackingInstance = (): typeof amplitude => {
  return window.amplitude;
};

export const initSessionReplay = async (): Promise<void> => {
  const analyticsTrackingInstance = getAnalyticsTrackingInstance();
  if (typeof window !== 'undefined' && analyticsTrackingInstance && AMPLITUDE_API_KEY) {
    analyticsTrackingInstance.getInstance().onInit(async () => {
      const { deviceId } = analyticsTrackingInstance.getInstance().options;
      const sessionId = analyticsTrackingInstance.getInstance().getSessionId();
      await sessionReplay.init(AMPLITUDE_API_KEY as string, {
        deviceId,
        sessionId,
        sampleRate: 0.3,
      }).promise;
      const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
      setTrack('Session Replay Initialized', {
        deviceId,
        sessionId,
        ...sessionReplayProperties,
      });
    });
  }
};

export const shutdownSessionReplay = (): void => {
  sessionReplay.shutdown();
};
